export const DEV_MODE_COOKIE = 'DEV_MODE';

export type DevRouteParam = {
  name: string;
  label: string;
  placeholder?: string;
};

export type DevRouteEntry = {
  path: string;
  description: string;
  params?: DevRouteParam[];
};

type WindowEnv = {
  _env_?: Record<string, string | undefined>;
};

export function isDevHost(hostname: string = window.location.hostname): boolean {
  const host = (hostname || '').toLowerCase();
  if (!host) return false;
  if (host === 'localhost' || host === '127.0.0.1') return true;
  return host.includes('dev.flow-front');
}

export function hasDevModeCookie(cookieSource: string = document.cookie): boolean {
  if (!cookieSource) return false;
  const parts = cookieSource.split(';');
  for (const part of parts) {
    const [rawName, ...rawValue] = part.trim().split('=');
    if (rawName === DEV_MODE_COOKIE && rawValue.join('=') === 'true') {
      return true;
    }
  }
  return false;
}

export function isDevUnlockEnvEnabled(
  env: Record<string, string | undefined> | undefined = (window as WindowEnv)._env_,
): boolean {
  return env?.REACT_APP_DEV_UNLOCK_ROUTES === 'true';
}

/** Fail-closed: host DEV + flag de ambiente + cookie DEV_MODE=true. */
export function isDevUnlockRoutesEnabled(): boolean {
  try {
    return (
      isDevHost() &&
      isDevUnlockEnvEnabled() &&
      hasDevModeCookie()
    );
  } catch {
    return false;
  }
}

export function buildDevRoutePath(
  pathTemplate: string,
  values: Record<string, string>,
): string {
  return pathTemplate.replace(/:([A-Za-z0-9_]+)\??/g, (_, key: string) => {
    const value = (values[key] || '').trim();
    return encodeURIComponent(value || key);
  });
}

export const DEV_ROUTES_CATALOG: DevRouteEntry[] = [
  { path: '/home', description: 'Início / dashboard' },
  { path: '/solicitacoes', description: 'Listagem de solicitações' },
  {
    path: '/solicitacoes/detalhes/:identificador',
    description: 'Detalhes da solicitação',
    params: [{ name: 'identificador', label: 'Identificador', placeholder: 'uuid-da-solicitacao' }],
  },
  {
    path: '/solicitacoes/cadastra-tramite/:identificador',
    description: 'Nova tramitação',
    params: [{ name: 'identificador', label: 'Identificador', placeholder: 'uuid-da-solicitacao' }],
  },
  {
    path: '/solicitacoes/:solicitacao_id/:orgao_id/documento/:solicitacao/cadastrar-documento',
    description: 'Cadastrar documento de resposta',
    params: [
      { name: 'solicitacao_id', label: 'ID solicitação' },
      { name: 'orgao_id', label: 'ID órgão' },
      { name: 'solicitacao', label: 'Identificador solicitação' },
    ],
  },
  {
    path: '/solicitacoes/:solicitacao_id/:orgao_id/documento/:solicitacao/editar-documento/:id_documento',
    description: 'Editar documento de resposta',
    params: [
      { name: 'solicitacao_id', label: 'ID solicitação' },
      { name: 'orgao_id', label: 'ID órgão' },
      { name: 'solicitacao', label: 'Identificador solicitação' },
      { name: 'id_documento', label: 'ID documento' },
    ],
  },
  {
    path: '/solicitacoes/:solicitacao_id/:orgao_id/documento/:id_document/visualizar',
    description: 'Visualizar documento de resposta',
    params: [
      { name: 'solicitacao_id', label: 'ID solicitação' },
      { name: 'orgao_id', label: 'ID órgão' },
      { name: 'id_document', label: 'ID documento' },
    ],
  },
  {
    path: '/solicitacoes/:identificador/:id/anexar-documento',
    description: 'Anexar documento',
    params: [
      { name: 'identificador', label: 'Identificador' },
      { name: 'id', label: 'ID' },
    ],
  },
  {
    path: '/solicitacoes/documento/:id/:identificador/visualizar-documentos',
    description: 'Visualizar documentos da solicitação',
    params: [
      { name: 'id', label: 'ID' },
      { name: 'identificador', label: 'Identificador' },
    ],
  },
  { path: '/fluxo', description: 'Listagem de fluxos' },
  { path: '/fluxo/cadastrar-fluxo', description: 'Cadastrar fluxo' },
  {
    path: '/fluxo/:flow_slug/editar',
    description: 'Editar fluxo',
    params: [{ name: 'flow_slug', label: 'Slug do fluxo' }],
  },
  {
    path: '/fluxo/copiar-fluxo/:flow_slug',
    description: 'Copiar fluxo',
    params: [{ name: 'flow_slug', label: 'Slug do fluxo' }],
  },
  {
    path: '/informacoes/:flow_slug',
    description: 'Informações do fluxo',
    params: [{ name: 'flow_slug', label: 'Slug do fluxo' }],
  },
  {
    path: '/fluxo/:flow_slug/cadastrar-formulario',
    description: 'Cadastrar formulário',
    params: [{ name: 'flow_slug', label: 'Slug do fluxo' }],
  },
  {
    path: '/fluxo/:flow_slug/form/:form_slug/editar',
    description: 'Editar formulário',
    params: [
      { name: 'flow_slug', label: 'Slug do fluxo' },
      { name: 'form_slug', label: 'Slug do formulário' },
    ],
  },
  {
    path: '/fluxo/formulario/:form_slug/:flow_slug/construir',
    description: 'Construtor de formulário',
    params: [
      { name: 'form_slug', label: 'Slug do formulário' },
      { name: 'flow_slug', label: 'Slug do fluxo' },
    ],
  },
  {
    path: '/fluxo/:flow_slug/tramitacao/construir',
    description: 'Construir tramitação',
    params: [{ name: 'flow_slug', label: 'Slug do fluxo' }],
  },
  {
    path: '/fluxo/:flow_slug/tramitacao/gerenciar',
    description: 'Gerenciar tramitação',
    params: [{ name: 'flow_slug', label: 'Slug do fluxo' }],
  },
  {
    path: '/fluxo/:flow_slug/termos/novo/:term_id?',
    description: 'Termos do fluxo',
    params: [
      { name: 'flow_slug', label: 'Slug do fluxo' },
      { name: 'term_id', label: 'ID do termo (opcional)' },
    ],
  },
  {
    path: '/fluxo/:flow_slug/workflow/salvar',
    description: 'Salvar workflow',
    params: [{ name: 'flow_slug', label: 'Slug do fluxo' }],
  },
  {
    path: '/fluxo/:flow_slug/documento/orgao/:orgao_id/configurar',
    description: 'Configurar documento',
    params: [
      { name: 'flow_slug', label: 'Slug do fluxo' },
      { name: 'orgao_id', label: 'ID órgão' },
    ],
  },
  {
    path: '/fluxo/:flow_slug/documento/orgao/:orgao_id/configurar/:id_document',
    description: 'Editar configuração de documento',
    params: [
      { name: 'flow_slug', label: 'Slug do fluxo' },
      { name: 'orgao_id', label: 'ID órgão' },
      { name: 'id_document', label: 'ID documento' },
    ],
  },
  { path: '/orgaos', description: 'Listagem de órgãos' },
  {
    path: '/orgaos/:orgao_id',
    description: 'Detalhes do órgão',
    params: [{ name: 'orgao_id', label: 'ID órgão' }],
  },
  {
    path: '/orgaos/:orgao_id/criar-cabecalho',
    description: 'Criar cabeçalho do órgão',
    params: [{ name: 'orgao_id', label: 'ID órgão' }],
  },
  {
    path: '/orgaos/:id/:orgao_id/editar',
    description: 'Editar cabeçalho do órgão',
    params: [
      { name: 'id', label: 'ID cabeçalho' },
      { name: 'orgao_id', label: 'ID órgão' },
    ],
  },
  { path: '/credenciais', description: 'Listagem de credenciais' },
  {
    path: '/credenciais/:id/detalhes',
    description: 'Detalhes da credencial',
    params: [{ name: 'id', label: 'ID credencial' }],
  },
  {
    path: '/credenciais/gerenciar/:id?',
    description: 'Gerenciar credencial',
    params: [{ name: 'id', label: 'ID credencial (opcional)' }],
  },
  { path: '/dev/rotas', description: 'Catálogo DEV de rotas (esta página)' },
];
